/**
 * Shift Roster → Reports
 *
 * Every report on the page is a projection of one flat fact table: `ShiftReportRecord`, which is
 * one employee on one date. Fifteen report types built as fifteen queries would have drifted —
 * "Night shifts" would end up meaning one thing on the Monthly report and another on the
 * Shift-wise one. Building the day-grain table once and aggregating it differently means a
 * filter, a preview total and a downloaded column all read the same number by construction.
 */

// ─── Report types ───

export type ShiftReportType =
  | 'DAILY'
  | 'WEEKLY'
  | 'MONTHLY'
  | 'EMPLOYEE_WISE'
  | 'DEPARTMENT_WISE'
  | 'MANAGER_WISE'
  | 'SHIFT_WISE'
  | 'HOLIDAY_WEEKEND'
  | 'UNASSIGNED';

/**
 * Whether a report lists days or summarises them.
 *
 * DETAIL rows are `ShiftReportRecord`s straight through; SUMMARY rows are one row per group with
 * shift counts. The distinction drives which column set the Select Columns panel offers, because
 * "Start Time" is meaningless on a row that spans a department for a month.
 */
export type ShiftReportShape = 'DETAIL' | 'SUMMARY';

export interface ShiftReportTypeMeta {
  id: ShiftReportType;
  label: string;
  description: string;
  shape: ShiftReportShape;
}

/**
 * The Report Type dropdown, in the order the requirement lists them.
 *
 * Four were removed on request, and with them the whole notion of a report that ships knowing it
 * cannot answer its own question:
 *
 *  - Shift Swap had no backing entity at all — nothing records an exchange between two employees,
 *    and a swap is indistinguishable from two independent overrides after the fact.
 *  - Attendance / Shift Summary could only ever ship half of itself: `attendanceCycleId` is a
 *    payroll-cycle tag, not a clock-in, so present / absent / late cannot be answered.
 *  - Shift Change read the assignment audit trail, which the deployed schema does not reliably
 *    serve.
 *  - Overtime joined a separate ledger that the roster's own columns no longer carry.
 *  - Employee Shift History and Complete Shift Roster were the two unfiltered views: both listed
 *    the same employee-days the Daily report does, one re-sorted by employee and one with every
 *    column ticked. Daily plus the Employee Name filter and Select Columns reaches either.
 *
 * The `unavailable` field went with them: it existed so a listed-but-empty report could explain
 * itself, and no report is listed on those terms any more. Every entry below answers its question
 * from the employee-day table and nothing else.
 */
export const SHIFT_REPORT_TYPES: ShiftReportTypeMeta[] = [
  { id: 'DAILY',            label: 'Daily Shift Report',           shape: 'DETAIL',  description: 'Every employee-day in the range, one row per day.' },
  { id: 'WEEKLY',           label: 'Weekly Shift Report',          shape: 'SUMMARY', description: 'Shift counts grouped by calendar week.' },
  { id: 'MONTHLY',          label: 'Monthly Shift Report',         shape: 'SUMMARY', description: 'Shift counts grouped by month.' },
  { id: 'EMPLOYEE_WISE',    label: 'Employee-wise Shift Report',   shape: 'SUMMARY', description: 'One row per employee with their shift breakdown.' },
  { id: 'DEPARTMENT_WISE',  label: 'Department-wise Shift Report',  shape: 'SUMMARY', description: 'One row per department with its shift breakdown.' },
  { id: 'MANAGER_WISE',     label: 'Manager-wise Shift Report',    shape: 'SUMMARY', description: "One row per manager, covering their reports' shifts." },
  { id: 'SHIFT_WISE',       label: 'Shift-wise Report',            shape: 'SUMMARY', description: 'One row per shift, with how many days it was worked.' },
  { id: 'HOLIDAY_WEEKEND',  label: 'Holiday / Weekend Shift Report', shape: 'DETAIL', description: 'Shifts falling on a public holiday or a weekly off.' },
  { id: 'UNASSIGNED',       label: 'Unassigned Shift Report',      shape: 'DETAIL',  description: 'Working days with no shift assigned and no primary shift to fall back on.' },
];

export const reportMeta = (id: ShiftReportType): ShiftReportTypeMeta =>
  SHIFT_REPORT_TYPES.find(r => r.id === id) || SHIFT_REPORT_TYPES[0];

// ─── Derived classifications ───

/**
 * The band a shift falls in, for the Shift Type filter and the preview's count tiles.
 *
 * Derived, because no shift record carries a "type". The order of the checks is the definition:
 * an explicitly configured night shift is Night whatever its clock time, the General shift is
 * named as such rather than being inferred from a 09:00 start it shares with a Morning shift,
 * and only then does the start hour decide. `OFF`, `HOLIDAY` and `LEAVE` are not clock bands at
 * all — they are the reasons a day has no shift, kept in the same field so one column can say
 * what the day was.
 */
export type ShiftBand =
  | 'Morning'
  | 'General'
  | 'Evening'
  | 'Night'
  | 'Weekly Off'
  | 'Holiday'
  | 'Leave'
  | 'Unassigned';

export const SHIFT_BANDS: ShiftBand[] = [
  'Morning', 'General', 'Evening', 'Night', 'Weekly Off', 'Holiday', 'Leave', 'Unassigned',
];

/** Where a day's shift came from. Distinguishes a real assignment from the employee's default. */
export type ShiftSource = 'Override' | 'Primary' | 'Weekly Off' | 'Holiday' | 'Leave' | 'Unassigned';

// ─── The fact table ───

/** One employee, one date. The grain every report is built from. */
export interface ShiftReportRecord {
  /** Cognito account id — the storage key for assignments. Never displayed as an employee ID. */
  employeeId: string;
  /** The HR employee number, which is what "Employee ID" means to a reader. */
  employeeCode: string;
  employeeName: string;
  employeeEmail: string;
  designation: string;
  /** Free-text Job Title (`custom:department`). Not the Department filter — see `team`. */
  jobTitle: string;
  /** Department / Team from the list HR maintains. What the Department filter matches. */
  team: string;
  location: string;
  managerEmail: string;
  employmentType: string;
  /** False for a disabled account or one whose exit date has passed. */
  employeeActive: boolean;
  dateOfJoining: string;
  dateOfExit: string;

  date: string;
  /** 0 = Sunday. */
  weekday: number;
  weekdayName: string;
  /** Month index, 0 = January, so callers need not re-parse `date`. */
  month: number;
  year: number;
  /** ISO week label, e.g. `2026-W34`. Groups the Weekly report. */
  isoWeek: string;
  /** A weekly off for *this* employee's shift, whose weekend days are configurable. */
  isWeekend: boolean;
  holidayName: string;
  isHoliday: boolean;

  shiftCode: string;
  shiftName: string;
  startTime: string;
  endTime: string;
  band: ShiftBand;
  source: ShiftSource;
  workingHours: number | null;
  breakDuration: number | null;
  overtimeEligible: boolean;
  /** True when a shift is actually worked — excludes offs, holidays, leave and unassigned days. */
  isWorkingDay: boolean;

  status: string;
  rosterStatus: string;
  previousShiftCode: string;
  changeReason: string;
  /**
   * True when this day's shift replaced a different one.
   *
   * No report reads it since Shift Change was removed. Kept on the fact table rather than deleted:
   * it comes free from the assignment row, and dropping it would take the audit trail out of the
   * table that every report is a projection of — putting it back would be a data change rather
   * than a report.
   */
  changed: boolean;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;

  leaveType: string;
  onLeave: boolean;

  overtimeHours: number;
  overtimeStatus: string;
  overtimeType: string;
}

// ─── Filters ───

/**
 * `''` on any field means "no constraint", so a blank filter form is the unfiltered report.
 *
 * Deliberately narrow. An earlier revision carried the full set the roster *could* be sliced by —
 * job title, designation, location, manager, employment type, shift type, start and end time,
 * assignment status, roster status, shift-change state, overtime, weekday/weekend and the whole
 * audit trail. They were removed on request: the page's job is core roster information, and
 * twenty-six inputs made the common questions ("this employee, this month") harder to reach than
 * the rare ones. The underlying fields all still exist on ShiftReportRecord, so restoring any of
 * them is a filter and a column, not a data change.
 */
export interface ShiftReportFilters {
  dateFrom: string;
  dateTo: string;
  /** Convenience pickers that *set* dateFrom/dateTo; -1 / 0 means unused. */
  month: number;
  year: number;

  employeeName: string;
  employeeCode: string;
  /** Department / Team, from the list HR maintains in Organisation Setup. */
  team: string;
  /** 'ALL' | 'ACTIVE' | 'INACTIVE' */
  employeeStatus: string;

  shiftCode: string;

  /** A specific weekday, 0 = Sunday; '' for any. */
  specificDay: string;
  /** 'ALL' | 'WORKING' | 'HOLIDAY' */
  holidayKind: string;
}

const currentYear = new Date().getFullYear();

export const emptyFilters = (): ShiftReportFilters => ({
  dateFrom: '',
  dateTo: '',
  month: -1,
  year: currentYear,

  employeeName: '',
  employeeCode: '',
  team: '',
  employeeStatus: 'ALL',

  shiftCode: '',

  specificDay: '',
  holidayKind: 'ALL',
});

/**
 * The month/year pair the page opens on: the current month.
 *
 * A Reports page whose default is "everything ever" would fire a year-wide fetch on first paint
 * for a report nobody asked for yet. One month is the same window Shift Details opens on.
 */
export const defaultFilters = (): ShiftReportFilters => {
  const now = new Date();
  return { ...emptyFilters(), month: now.getMonth(), year: now.getFullYear() };
};

// ─── Preview summary ───

/** The tiles above the preview table. Counted from the filtered records, never from the page slice. */
export interface ShiftReportSummary {
  totalEmployees: number;
  totalShifts: number;
  morning: number;
  general: number;
  evening: number;
  night: number;
  unassigned: number;
  changed: number;
  weeklyOff: number;
  holiday: number;
  onLeave: number;
  overtimeHours: number;
}
